/** @file createPuppetInstance: a loaded bundle as a puppet in a Babylon scene, driven per frame. */
import type { Scene } from '@babylonjs/core/scene.js';
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { worldPointOf } from '@zilo/pose3d-render-core/place/worldPointOf';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { LoadedBundle } from '@zilo/pose3d-runtime-babylon/bundle/loadedBundle';
import { startClock } from '@zilo/pose3d-runtime-babylon/clip/startClock';
import { stepClock } from '@zilo/pose3d-runtime-babylon/clip/stepClock';
import { instanceCore } from '@zilo/pose3d-runtime-babylon/instance/instanceCore';
import { instanceState } from '@zilo/pose3d-runtime-babylon/instance/instanceState';
import type { InstanceCore, PuppetInstance, PuppetInstanceOptions } from '@zilo/pose3d-runtime-babylon/instance/puppetInstanceTypes';
import { updateInstance } from '@zilo/pose3d-runtime-babylon/instance/updateInstance';

/** Starts `name` (unknown -> NOT_FOUND at /clips/<name>); the same clip keeps playing unless `restart`. */
function play(core: InstanceCore, name: string, opts: { blend?: number; restart?: boolean }): void {
  const clip = Object.hasOwn(core.bundle.clips, name) ? core.bundle.clips[name] : undefined;
  if (clip === undefined) return throwError(ERROR_CODES.NOT_FOUND, { clip: name }, { path: `/clips/${name}`, hint: Object.keys(core.bundle.clips).join(', ') });
  if (core.clock?.clip === clip && opts.restart !== true) return undefined;
  core.clock = startClock(clip, core.clock, opts.blend ?? core.blendSeconds);
  core.events = 'fresh';
  return undefined;
}

/** The entity origin plus the pose position of the socket's node, or null for an unknown socket. */
function socketWorld(core: InstanceCore, socket: string): Vec3 | null {
  const node = core.puppet.sockets[socket];
  const index = node === undefined ? undefined : core.rig.index.get(node);
  if (index === undefined) return null;
  const p = worldPointOf(core.last.pose, index, [0, 0, 0], core.profile.zScaleRotate ?? 1);
  return [core.origin[0] + p[0], core.origin[1] + p[1], core.origin[2] + p[2]];
}

/**
 * A `PuppetInstance` over `bundle` in `scene`: `update(dt, camera)` runs the frame (`updateInstance`); `play` starts a
 * clip blending the previous one out; heading, facing steps, origin, variant mask, attachment group, toon, pause and
 * frame stepping are setters read by the next update. The CPU corner path draws; `drawPath: 'gpu'` is not built yet
 * (plan Task 14, gated) and draws through the CPU path too.
 */
export async function createPuppetInstance(scene: Scene, bundle: LoadedBundle, opts: PuppetInstanceOptions = {}): Promise<PuppetInstance> {
  const core = await instanceCore(scene, bundle, opts);
  return {
    bundle,
    play: (name, o = {}) => { play(core, name, o); },
    setHeading: (deg) => { core.heading = deg; },
    setFacingSteps: (steps) => { core.steps = steps; },
    setOrigin: (p) => { core.origin = [p[0], p[1], p[2]]; },
    setVariant: (mask) => { core.variantMask = mask; },
    setAttachmentGroup: (group) => { core.weaponGroup = group; },
    setToon: (on) => { core.joints?.setToon(on); },
    setPaused: (on) => { if (core.clock !== null) core.clock = { ...core.clock, paused: on }; },
    stepFrames: (n) => {
      if (core.clock === null) return;
      core.clock = stepClock(core.clock, n);
      core.events = 'normal';
    },
    update: (dt, camera) => { updateInstance(core, dt, camera); },
    socketWorld: (socket) => socketWorld(core, socket),
    state: () => instanceState(core),
    dispose: () => {
      core.draw.dispose();
      core.joints?.dispose();
    },
  };
}
